import React from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { 
  X, 
  User, 
  Building, 
  Calendar, 
  Activity, 
  AlertTriangle,
  Clock,
  FileText,
  Download,
  Edit,
  Trash2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Assessment } from '../../services/api';
import { authenticatedLaceToolAPI } from '../../services/authenticatedApi';

interface AssessmentDetailsModalProps {
  assessment: Assessment;
  onClose: () => void;
  onDelete?: () => void;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  position: relative;
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  padding: 24px;
  border-radius: 16px 16px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:focus {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }
`;

const ModalBody = styled.div`
  padding: 32px;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e5e7eb;
`;

const SectionIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
`;

const InfoCard = styled.div`
  background: #f8fafc;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid #e5e7eb;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const InfoValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const ScoreSection = styled.div`
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 2px solid #22c55e;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  margin: 24px 0;
`;

const ScoreDisplay = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
`;

const ScoreLabel = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #16a34a;
  margin-bottom: 12px;
`;

const RiskIndicator = styled.div<{ risk: 'low' | 'moderate' | 'high' | 'very-high' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 25px;
  font-weight: 600;
  font-size: 16px;

  ${props => {
    switch (props.risk) {
      case 'low':
        return 'background: #d1fae5; color: #065f46;';
      case 'moderate':
        return 'background: #fef3c7; color: #92400e;';
      case 'high':
        return 'background: #fed7aa; color: #9a3412;';
      case 'very-high':
        return 'background: #fee2e2; color: #991b1b;';
      default:
        return 'background: #f3f4f6; color: #374151;';
    }
  }}
`;

const ReadmissionProbability = styled.div`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #0ea5e9;
  border-radius: 12px;
  padding: 16px 24px;
  text-align: center;
  margin-top: 16px;
`;

const ProbabilityLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0369a1;
  margin-bottom: 4px;
`;

const ProbabilityValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #0c4a6e;
`;

const ScoringDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 20px;
`;

const ScoringCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  text-align: center;
`;

const ScoringTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 8px;
`;

const ScoringValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #3b82f6;
  margin-bottom: 4px;
`;

const ScoringDescription = styled.div`
  font-size: 12px;
  color: #9ca3af;
`;

const ComorbiditiesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const ComorbidityTag = styled.span`
  background: #eff6ff;
  color: #1e40af;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #bfdbfe;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 2px solid #e5e7eb;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 2px solid;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          border-color: #3b82f6;
          &:hover {
            background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
            transform: translateY(-2px);
          }
        `;
      case 'danger':
        return `
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          border-color: #ef4444;
          &:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-2px);
          }
        `;
      default:
        return `
          background: white;
          color: #374151;
          border-color: #d1d5db;
          &:hover {
            background: #f9fafb;
            border-color: #9ca3af;
          }
        `;
    }
  }}

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const AssessmentDetailsModal: React.FC<AssessmentDetailsModalProps> = ({
  assessment,
  onClose,
  onDelete
}) => {
  const getRiskLevel = (score: number): 'low' | 'moderate' | 'high' | 'very-high' => {
    if (score <= 4) return 'low';
    if (score <= 6) return 'moderate';
    if (score <= 9) return 'high';
    return 'very-high';
  };

  const getRiskLabel = (score: number): string => {
    if (score <= 4) return 'Low Risk';
    if (score <= 6) return 'Moderate Risk';
    if (score <= 9) return 'High Risk';
    return 'Very High Risk';
  };

  const getReadmissionProbability = (score: number): string => {
    const probabilityMap: { [key: number]: string } = {
      0: '0%',
      1: '1%',
      2: '2%',
      3: '3%',
      4: '4%',
      5: '5%',
      6: '6%',
      7: '7%',
      8: '9%',
      9: '10%',
      10: '12%',
      11: '14%',
      12: '17%',
      13: '20%',
      14: '23%',
      15: '27%',
      16: '30%',
      17: '35%',
      18: '39%',
      19: '44%'
    };
    
    // For scores above 19, use the highest known probability
    if (score >= 19) return '44%+';
    
    return probabilityMap[score] || 'Unknown';
  };

  const riskLevel = getRiskLevel(assessment.totalScore);
  const riskLabel = getRiskLabel(assessment.totalScore);
  const readmissionProbability = getReadmissionProbability(assessment.totalScore);

  const handleEdit = () => {
    // Navigate to edit assessment
    window.location.href = `/assessments/edit/${assessment.id}`;
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete the assessment for ${assessment.patientName}? This action cannot be undone.`)) {
      return;
    }

    const loadingToast = toast.loading('Deleting assessment...');
    
    try {
      await authenticatedLaceToolAPI.deleteAssessment(assessment.id);
      toast.success('Assessment deleted successfully!', { id: loadingToast });
      
      // Call the onDelete callback if provided (to refresh the list)
      if (onDelete) {
        onDelete();
      }
      
      // Close the modal
      onClose();
    } catch (error: any) {
      console.error('Error deleting assessment:', error);
      
      let errorMessage = 'Failed to delete assessment. Please try again.';
      
      if (error.response) {
        const status = error.response.status;
        if (status === 404) {
          errorMessage = 'Assessment not found. It may have already been deleted.';
        } else if (status === 403) {
          errorMessage = 'You do not have permission to delete this assessment.';
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      toast.error(errorMessage, { id: loadingToast });
    }
  };

  const handleDownload = async () => {
    const loadingToast = toast.loading('Processing PDF download...');
    
    try {
      console.log('Starting PDF download for assessment:', assessment.id);
      
      const blob = await authenticatedLaceToolAPI.downloadAssessmentPDF(assessment.id);
      
      if (!blob || blob.size === 0) {
        throw new Error('Received empty or invalid PDF file');
      }
      
      console.log('PDF blob received, size:', blob.size, 'bytes');
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const date = new Date(assessment.createdAt).toISOString().split('T')[0];
      const patientName = assessment.patientName.replace(/[^a-zA-Z0-9]/g, '_');
      link.download = `LACE_Assessment_${patientName}_${date}_${assessment.id}.pdf`;
      
      console.log('Downloading file:', link.download);
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('PDF download completed successfully');
      toast.success('Assessment PDF download started!', { id: loadingToast });
    } catch (error: any) {
      console.error('Error downloading PDF:', error);
      
      let errorMessage = 'Failed to download PDF. Please try again.';
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;
        
        switch (status) {
          case 401:
            errorMessage = 'Authentication failed. Please log in again.';
            break;
          case 404:
            errorMessage = 'Assessment not found. It may have been deleted.';
            break;
          case 500:
            errorMessage = 'Server error during PDF generation. Please try again later.';
            break;
          default:
            errorMessage = data?.message || `Server error (${status}). Please try again.`;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Other error
        errorMessage = error.message || 'Unknown error occurred.';
      }
      
      toast.error(errorMessage, { id: loadingToast });
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <FileText size={24} />
            Assessment Details
          </ModalTitle>
          <CloseButton onClick={onClose} aria-label="Close modal">
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <Section>
            <SectionHeader>
              <SectionIcon>
                <User size={20} />
              </SectionIcon>
              <SectionTitle>Patient Information</SectionTitle>
            </SectionHeader>
            
            <InfoGrid>
              <InfoCard>
                <InfoLabel>Patient Name</InfoLabel>
                <InfoValue>{assessment.patientName}</InfoValue>
              </InfoCard>
              
              <InfoCard>
                <InfoLabel>Facility</InfoLabel>
                <InfoValue>{assessment.facilityName}</InfoValue>
              </InfoCard>
              
              <InfoCard>
                <InfoLabel>Date of Birth</InfoLabel>
                <InfoValue>
                  {format(parseISO(assessment.dateOfBirth), 'MMM dd, yyyy')}
                </InfoValue>
              </InfoCard>
              
              <InfoCard>
                <InfoLabel>Admission Date</InfoLabel>
                <InfoValue>
                  {format(parseISO(assessment.admissionDate), 'MMM dd, yyyy')}
                </InfoValue>
              </InfoCard>
            </InfoGrid>
          </Section>

          <ScoreSection>
            <ScoreDisplay>{readmissionProbability}</ScoreDisplay>
            <ScoreLabel>Expected Probability of Readmission</ScoreLabel>
            <RiskIndicator risk={riskLevel}>
              <AlertTriangle size={20} />
              {riskLabel}
            </RiskIndicator>
            <ReadmissionProbability>
              <ProbabilityLabel>Total LACE Score</ProbabilityLabel>
              <ProbabilityValue>{assessment.totalScore}</ProbabilityValue>
            </ReadmissionProbability>
          </ScoreSection>

          <Section>
            <SectionHeader>
              <SectionIcon>
                <Activity size={20} />
              </SectionIcon>
              <SectionTitle>Scoring Breakdown</SectionTitle>
            </SectionHeader>
            
            <ScoringDetails>
              <ScoringCard>
                <ScoringTitle>Length of Stay</ScoringTitle>
                <ScoringValue>{assessment.lengthOfStayPoints}</ScoringValue>
                <ScoringDescription>{assessment.lengthOfStay}</ScoringDescription>
              </ScoringCard>
              
              <ScoringCard>
                <ScoringTitle>Acute Admission</ScoringTitle>
                <ScoringValue>{assessment.acuteAdmissionPoints}</ScoringValue>
                <ScoringDescription>{assessment.acuteAdmission}</ScoringDescription>
              </ScoringCard>
              
              <ScoringCard>
                <ScoringTitle>Comorbidities</ScoringTitle>
                <ScoringValue>{assessment.comorbidityPoints}</ScoringValue>
                <ScoringDescription>Max 6 points</ScoringDescription>
              </ScoringCard>
              
              <ScoringCard>
                <ScoringTitle>Emergency Visits</ScoringTitle>
                <ScoringValue>{assessment.emergencyVisitsPoints}</ScoringValue>
                <ScoringDescription>{assessment.emergencyVisits} visits</ScoringDescription>
              </ScoringCard>
            </ScoringDetails>
          </Section>

          {assessment.comorbidities && assessment.comorbidities.length > 0 && (
            <Section>
              <SectionHeader>
                <SectionIcon>
                  <AlertTriangle size={20} />
                </SectionIcon>
                <SectionTitle>Comorbidities</SectionTitle>
              </SectionHeader>
              
              <ComorbiditiesList>
                {assessment.comorbidities.map((comorbidity, index) => (
                  <ComorbidityTag key={index}>
                    {comorbidity}
                  </ComorbidityTag>
                ))}
              </ComorbiditiesList>
            </Section>
          )}

          <Section>
            <SectionHeader>
              <SectionIcon>
                <Clock size={20} />
              </SectionIcon>
              <SectionTitle>Assessment Details</SectionTitle>
            </SectionHeader>
            
            <InfoGrid>
              <InfoCard>
                <InfoLabel>Assessed By</InfoLabel>
                <InfoValue>{assessment.assessedBy || 'Unknown'}</InfoValue>
              </InfoCard>
              
              <InfoCard>
                <InfoLabel>Assessment Date</InfoLabel>
                <InfoValue>
                  {format(parseISO(assessment.createdAt), 'MMM dd, yyyy HH:mm')}
                </InfoValue>
              </InfoCard>
              
              <InfoCard>
                <InfoLabel>Last Updated</InfoLabel>
                <InfoValue>
                  {format(parseISO(assessment.updatedAt), 'MMM dd, yyyy HH:mm')}
                </InfoValue>
              </InfoCard>
              
              <InfoCard>
                <InfoLabel>Assessment ID</InfoLabel>
                <InfoValue style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                  {assessment.id}
                </InfoValue>
              </InfoCard>
            </InfoGrid>
          </Section>

          {assessment.notes && (
            <Section>
              <SectionHeader>
                <SectionIcon>
                  <FileText size={20} />
                </SectionIcon>
                <SectionTitle>Notes</SectionTitle>
              </SectionHeader>
              
              <InfoCard>
                <InfoValue>{assessment.notes}</InfoValue>
              </InfoCard>
            </Section>
          )}

          <ActionButtons>
            <ActionButton variant="primary" onClick={handleEdit}>
              <Edit size={16} />
              Edit Assessment
            </ActionButton>
            
            <ActionButton onClick={handleDownload}>
              <Download size={16} />
              Download PDF
            </ActionButton>
            
            <ActionButton variant="danger" onClick={handleDelete}>
              <Trash2 size={16} />
              Delete
            </ActionButton>
          </ActionButtons>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AssessmentDetailsModal;
